import React, { useEffect, useState, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Images } from 'lucide-react';

const Lightbox = ({ open, onClose, images = [], title, subtitle }) => {
  const [index, setIndex] = useState(0);

  // Reset index when opening a new project
  useEffect(() => {
    if (open) setIndex(0);
  }, [open, images]);

  const total = images.length;
  const prev = useCallback(() => setIndex((i) => (i - 1 + total) % total), [total]);
  const next = useCallback(() => setIndex((i) => (i + 1) % total), [total]);

  // Keyboard navigation + body scroll lock
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && total > 1) prev();
      if (e.key === 'ArrowRight' && total > 1) next();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, prev, next, total]);

  if (!open || total === 0) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/92 backdrop-blur-sm flex items-center justify-center animate-in fade-in duration-200"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 px-6 lg:px-10 py-5 flex items-center justify-between z-10" onClick={(e) => e.stopPropagation()}>
        <div className="text-white">
          <h4 className="text-[13px] md:text-[14px] font-bold tracking-[0.18em]">{title}</h4>
          {subtitle && <p className="text-[11px] md:text-[12px] text-white/60 mt-1">{subtitle}</p>}
        </div>
        <div className="flex items-center gap-4">
          {total > 1 && (
            <span className="text-white/70 text-[12px] tracking-[0.15em] flex items-center gap-2">
              <Images size={14} />
              {index + 1} / {total}
            </span>
          )}
          <button
            onClick={onClose}
            aria-label="Zamknij"
            className="w-10 h-10 flex items-center justify-center text-white/80 hover:text-white border border-white/15 hover:border-white/40 transition-colors duration-200"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Prev button */}
      {total > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); prev(); }}
          aria-label="Poprzednie"
          className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white border border-white/15 hover:border-white/50 bg-black/30 transition-colors duration-200 z-10"
        >
          <ChevronLeft size={26} />
        </button>
      )}

      {/* Image */}
      <div
        className="relative max-w-[92vw] max-h-[80vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          key={images[index]}
          src={images[index]}
          alt={`${title} ${index + 1}`}
          className="max-w-[92vw] max-h-[80vh] object-contain shadow-2xl animate-in fade-in zoom-in-95 duration-200"
        />
      </div>

      {/* Next button */}
      {total > 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); next(); }}
          aria-label="Następne"
          className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-white/80 hover:text-white border border-white/15 hover:border-white/50 bg-black/30 transition-colors duration-200 z-10"
        >
          <ChevronRight size={26} />
        </button>
      )}

      {/* Thumbnails */}
      {total > 1 && (
        <div
          className="absolute bottom-6 left-0 right-0 flex items-center justify-center gap-2 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((img, i) => (
            <button
              key={img + i}
              onClick={() => setIndex(i)}
              className={`w-14 h-10 overflow-hidden border-2 transition-all duration-200 ${
                i === index ? 'border-[#e30613] opacity-100' : 'border-white/20 opacity-60 hover:opacity-90'
              }`}
              aria-label={`Zdjęcie ${i + 1}`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default Lightbox;
